import React, { Component, ReactNode } from "react";
import { Result, Button } from "antd";
import { FirebaseError } from "firebase/app";

function handleFirebaseError(error: FirebaseError): string {
    switch (error.code) {
        case "auth/network-request-failed":
            return "Network error. Please check your connection.";
        case "auth/invalid-credential":
            return "Invalid login. Please try again.";
        case "permission-denied":
        case "firestore/permission-denied":
            return "You don’t have permission to access this data.";
        case "invalid-argument":
        case "firestore/invalid-argument":
            return "Invalid request. Please contact support.";
        default:
            return "A Firebase error occurred. Please try again.";
    }
}

interface ErrorHandlerProps {
    children: ReactNode;
}

interface ErrorHandlerState {
    hasError: boolean;
    errorMessage?: string;
}

// TODO: Use function component
export default class ErrorHandler extends Component<ErrorHandlerProps, ErrorHandlerState> {
    constructor(props: ErrorHandlerProps) {
        super(props);
        this.state = { hasError: false, errorMessage: undefined };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidMount() {
        window.onerror = (msg, url, lineNo, columnNo, error) => {
            console.log("Global Error:", { msg, url, lineNo, columnNo, error });
            this.setState({
                hasError: true,
                errorMessage: "Something went wrong. Please try again later.",
            });
            return false;
        };

        window.onunhandledrejection = (event) => {
            console.error("Unhandled Rejection:", event.reason);

            if (event.reason instanceof FirebaseError) {
                this.setState({
                    hasError: true,
                    errorMessage: handleFirebaseError(event.reason),
                });
            } else {
                this.setState({
                    hasError: true,
                    errorMessage: "An unexpected error occurred. Please try again.",
                });
            }
        };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.log("React Render Error:", error, info);

        if (error instanceof FirebaseError) {
            this.setState({ hasError: true, errorMessage: handleFirebaseError(error) });
        } else {
            this.setState({
                hasError: true,
                errorMessage: "Something went wrong. Please try again later.",
            });
        }
    }

    handleReload = () => {
        this.setState({ hasError: false, errorMessage: undefined });
        window.location.reload();
    };

    render() {
        const { hasError, errorMessage } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <Result
                    status="500"
                    title="Something went wrong"
                    subTitle={errorMessage}
                    extra={[
                        <Button type="primary" key="reload" onClick={this.handleReload}>
                            Reload Page
                        </Button>,
                        <Button key="home" type="default">
                            <a href="/">Back Home</a>
                        </Button>,
                    ]}
                />
            );
        }

        return children;
    }
}
